import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import GUI from "lil-gui";
import vertexShader from "./shaders/water/vertex.glsl";
import fragmentShader from "./shaders/water/fragment.glsl";
/**
 * Base
 */
// Debug
const gui = new GUI({ width: 340 });
const debugObject = {
	freezeTime: false,
	enableLight: true,
};

// Canvas
const canvas = document.querySelector("canvas.webgl");

// Scene
const scene = new THREE.Scene();
// scene.background = new THREE.Color(0x262837);

// Axes helper
// const axesHelper = new THREE.AxesHelper();
// axesHelper.position.y += 0.25;
// scene.add(axesHelper);

/**
 * Water
 */
// Geometry
// const waterGeometry = new THREE.RingGeometry(0.5, 2, 512, 512);
const waterGeometry = new THREE.PlaneGeometry(4, 4, 512, 512);
waterGeometry.deleteAttribute("normal");
waterGeometry.deleteAttribute("uv");

// Color
// debugObject.depthColor = "#ff4000";
// debugObject.surfaceColor = "#151c37";

debugObject.surfaceColor = "#9bd8ff";
debugObject.depthColor = "#186691";

debugObject.lightColor = "#cdecf4";

// Material
const waterMaterial = new THREE.ShaderMaterial({
	vertexShader: vertexShader,
	fragmentShader: fragmentShader,
	side: THREE.DoubleSide,
	// transparent: true,
	// wireframe: true,
	uniforms: {
		uTime: { value: 0 },

		uBigWavesElevation: { value: 0.2 },
		uBigWavesFrequency: { value: new THREE.Vector2(4, 1.5) },
		uBigWavesSpeed: { value: 1 },

		uDepthColor: { value: new THREE.Color(debugObject.depthColor) },
		uSurfaceColor: { value: new THREE.Color(debugObject.surfaceColor) },
		uColorOffset: { value: 0.25 },
		uColorMultiplier: { value: 1 },

		uSmallWavesElevation: { value: 0.05 },
		uSmallWavesFrequency: { value: 1.6 },
		uSmallWavesSpeed: { value: 0.36 },
		uSmallIterations: { value: 3 },

		uShift: { value: 0.008 },
		uEnableLight: { value: true },
		uLightColor: { value: new THREE.Color(debugObject.lightColor) },
		uLightIntensity: { value: 10 },
		uLightSpecular: { value: 0.5 },
		uLightDecay: { value: 0.5 },
	},
});

const guiColor = gui.addFolder("Color");
const guiBW = gui.addFolder("Big Waves");
const guiSW = gui.addFolder("Small Waves");
const guiLight = gui.addFolder("Light");
guiBW
	.add(waterMaterial.uniforms.uBigWavesElevation, "value")
	.min(0)
	.max(1)
	.step(0.001)
	.name("Big Waves Elevation");
guiBW
	.add(waterMaterial.uniforms.uBigWavesFrequency.value, "x")
	.min(0)
	.max(10)
	.step(0.001)
	.name("Big Waves Frequency X");
guiBW
	.add(waterMaterial.uniforms.uBigWavesFrequency.value, "y")
	.min(0)
	.max(10)
	.step(0.001)
	.name("Big Waves Frequency Y");
guiBW
	.add(waterMaterial.uniforms.uBigWavesSpeed, "value")
	.min(0)
	.max(4)
	.step(0.001)
	.name("Big Waves Speed");

guiColor.addColor(debugObject, "surfaceColor").onChange(() => {
	waterMaterial.uniforms.uSurfaceColor.value.set(debugObject.surfaceColor);
});
guiColor.addColor(debugObject, "depthColor").onChange(() => {
	waterMaterial.uniforms.uDepthColor.value.set(debugObject.depthColor);
});
guiColor
	.add(waterMaterial.uniforms.uColorOffset, "value")
	.min(0)
	.max(1)
	.step(0.001)
	.name("Color Offset");
guiColor
	.add(waterMaterial.uniforms.uColorMultiplier, "value")
	.min(0)
	.max(5)
	.step(0.001)
	.name("Color Multiplier");
guiSW
	.add(waterMaterial.uniforms.uSmallWavesElevation, "value")
	.min(0)
	.max(1)
	.step(0.001)
	.name("Small Waves Elevation");
guiSW
	.add(waterMaterial.uniforms.uSmallWavesFrequency, "value")
	.min(0)
	.max(30)
	.step(0.001)
	.name("Small Waves Frequency");
guiSW
	.add(waterMaterial.uniforms.uSmallWavesSpeed, "value")
	.min(0)
	.max(4)
	.step(0.001)
	.name("Small Waves Speed");
guiSW
	.add(waterMaterial.uniforms.uSmallIterations, "value")
	.min(0)
	.max(6)
	.step(1)
	.name("Small Waves Iterations");
guiLight
	.add(waterMaterial.uniforms.uShift, "value")
	.min(0.0001)
	.max(0.1)
	.step(0.0001)
	.name("Shift");
guiLight.add(waterMaterial.uniforms.uEnableLight, "value").name("Show Light");
guiLight
	.addColor(debugObject, "lightColor")
	.name("Light Color")
	.onChange(() => {
		waterMaterial.uniforms.uLightColor.value.set(debugObject.lightColor);
	});
guiLight
	.add(waterMaterial.uniforms.uLightIntensity, "value")
	.min(0.01)
	.max(50)
	.step(0.01)
	.name("Light Intensity");
guiLight
	.add(waterMaterial.uniforms.uLightSpecular, "value")
	.min(0.001)
	.max(20)
	.step(0.001)
	.name("Light Specular");
guiLight
	.add(waterMaterial.uniforms.uLightDecay, "value")
	.min(0)
	.max(1)
	.step(0.01)
	.name("Light Decay");

gui.add(debugObject, "freezeTime").name("Freeze Time");

// Mesh
const water = new THREE.Mesh(waterGeometry, waterMaterial);
water.rotation.x = -Math.PI * 0.5;
scene.add(water);

/**
 * Sizes
 */
const sizes = {
	width: window.innerWidth,
	height: window.innerHeight,
};

window.addEventListener("resize", () => {
	// Update sizes
	sizes.width = window.innerWidth;
	sizes.height = window.innerHeight;

	// Update camera
	camera.aspect = sizes.width / sizes.height;
	camera.updateProjectionMatrix();

	// Update renderer
	renderer.setSize(sizes.width, sizes.height);
	renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
});

/**
 * Camera
 */
// Base camera
const camera = new THREE.PerspectiveCamera(
	75,
	sizes.width / sizes.height,
	0.1,
	100,
);
camera.position.set(1.75, 1.4, 1.75);
scene.add(camera);

// Controls
const controls = new OrbitControls(camera, canvas);
controls.enableDamping = true;

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
	canvas: canvas,
	antialias: true,
});
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.setSize(sizes.width, sizes.height);
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

/**
 * Animate
 */
const clock = new THREE.Clock();
let elapsedTime = 0;

const tick = () => {
	const deltaTime = clock.getDelta();

	if (!debugObject.freezeTime) {
		elapsedTime += deltaTime;
	}

	// Update water
	waterMaterial.uniforms.uTime.value = elapsedTime;

	// Update controls
	controls.update();

	// Render
	renderer.render(scene, camera);

	// Call tick again on the next frame
	window.requestAnimationFrame(tick);
};

tick();
